if (!customElements.get('wls-slider')) {
  class WLSlider extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        slider: 'js-wls-slider',
        nextButton: 'js-wls-slider-next',
        prevButton: 'js-wls-slider-prev',
        sliderSettings: `SliderSettings-${this.getAttribute('data-id')}`,
        pagination: 'js-wls-slider-pagination',
        navigation: 'js-slider-navigation',
        swiperSlide: 'swiper-slide',
        video: 'wls-video',
        playing: 'playing',
        hidden: 'hidden',
        locked: 'wls-slider--locked',
        fallback: 'wls-slider--fallback',
      };

      this.swiperInstance = null;
      this.resizeTimeout = null;
      this.isReinitializing = false;

      // Bind methods
      this.boundSliderReinitHandler = this.reInit.bind(this);
      this.boundHandleResize = this._handleResize.bind(this);

      this.paginationType = this.getAttribute('data-pagination-type') || 'bullets';
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        slider: this.querySelector(`.${this.classes.slider}`),
        nextButtons: this.querySelectorAll(`.${this.classes.nextButton}`),
        prevButtons: this.querySelectorAll(`.${this.classes.prevButton}`),
        paginations: this.querySelectorAll(`.${this.classes.pagination}`),
        navigations: this.querySelectorAll(`.${this.classes.navigation}`),
        slides: this.querySelectorAll(`.${this.classes.swiperSlide}`),
      };
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      this._init();
      window.addEventListener('resize', this.boundHandleResize);
      this.addEventListener('wls:slider:reinit', this.boundSliderReinitHandler);
    }

    /**
     * Reinitialize the slider (public method for external use)
     * @returns {void}
     */
    reInit() {
      // Prevent multiple simultaneous reinitializations
      if (this.isReinitializing) {
        return;
      }

      this.isReinitializing = true;

      try {
        this.disconnectedCallback();
        this._init();
        this._handleResize();
      } catch (error) {
        console.error('Error during slider reinitialization:', error);
      } finally {
        this.isReinitializing = false;
      }
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      // Clear any pending timeouts
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = null;
      }

      this._destroy();
      this.classList.remove(this.classes.fallback);
      this.style.removeProperty('--fallback-slides-per-view');
      this.style.removeProperty('--fallback-gap');
      window.removeEventListener('resize', this.boundHandleResize);
      this.removeEventListener('wls:slider:reinit', this.boundSliderReinitHandler);
    }

    /**
     * Initialize the slider
     * @returns {void}
     */
    _init() {
      // Check if Swiper library is loaded
      if (typeof Swiper === 'undefined') {
        console.error('Swiper library is not loaded. Please include Swiper before initializing the slider.');
        return;
      }

      // Verify slider element exists
      if (!this.elements.slider) {
        console.error('Slider element not found. Check your HTML structure.');
        return;
      }

      const totalSlides = this.elements.slides?.length || 0;

      // Guard against no slides
      if (totalSlides === 0) {
        console.warn('No slides found in slider');
        return;
      }

      const {
        customSettings,
        slidesPerView,
        loop,
        centeredSlides,
        spaceBetween,
        navigation,
        pagination,
        autoplay,
        insufficientSlidesPosition,
      } = this._getSliderSettingsFromLiquid();

      if (this._shouldUseFallbackLayout(slidesPerView, totalSlides)) {
        this._applyFallbackLayout(slidesPerView, spaceBetween);
        return;
      }

      const shouldCenterSlides = {
        base: totalSlides > slidesPerView.base * 1 + 2,
        tablet: totalSlides >= slidesPerView.tablet * 1 + 2,
        desktop: totalSlides >= slidesPerView.desktop * 1 + 1.5,
      };

      const resolveLoop = (configValue, slidesPerViewValue) => {
        if (slidesPerViewValue === 'auto') return configValue;
        return configValue ? totalSlides > slidesPerViewValue : false;
      };

      try {
        this.swiperInstance = new Swiper(this.elements.slider, {
          slidesPerView: slidesPerView.base ?? 1.3,
          spaceBetween: spaceBetween.base ?? 16,
          loop: resolveLoop(loop.base, slidesPerView.base ?? 1.3),
          centeredSlides: centeredSlides.base ? shouldCenterSlides.base : false,
          ...customSettings,
          centerInsufficientSlides: insufficientSlidesPosition.base === 'center' ? true : false,
          navigation: {
            nextEl: [...this.elements.nextButtons],
            prevEl: [...this.elements.prevButtons],
            enabled: navigation?.base ?? false,
          },
          pagination: {
            el: [...this.elements.paginations],
            clickable: true,
            enabled: pagination?.base ?? false,
            type: this.paginationType === 'scrollbar' ? 'progressbar' : 'bullets',
          },
          threshold: 4,
          grabCursor: true,
          autoplay: autoplay?.base
            ? {
                delay: autoplay?.time ?? 3000,
                disableOnInteraction: false,
              }
            : false,
          on: {
            slideChange: this._handleSlideChange.bind(this),
            init: () => {
              this.elements.navigations.forEach((navigation) => {
                navigation.classList.remove(this.classes.hidden);
              });
            },
            lock: () => {
              this.classList.add(this.classes.locked);
            },
            unlock: () => {
              this.classList.remove(this.classes.locked);
            },
          },
          breakpoints: {
            768: {
              slidesPerView: slidesPerView.tablet ?? 2,
              spaceBetween: spaceBetween.tablet ?? 16,
              loop: resolveLoop(loop.tablet, slidesPerView.tablet ?? 2),
              centeredSlides: centeredSlides.tablet ? shouldCenterSlides.tablet : false,
              centerInsufficientSlides: insufficientSlidesPosition.tablet === 'center' ? true : false,
              navigation: {
                enabled: navigation?.tablet ?? false,
              },
              pagination: {
                enabled: pagination?.tablet ?? false,
              },
            },
            1024: {
              slidesPerView: slidesPerView.desktop ?? 4,
              spaceBetween: spaceBetween.desktop ?? 24,
              loop: resolveLoop(loop.desktop, slidesPerView.desktop ?? 4),
              centeredSlides: centeredSlides.desktop ? shouldCenterSlides.desktop : false,
              centerInsufficientSlides: insufficientSlidesPosition.desktop === 'center' ? true : false,
              navigation: {
                enabled: navigation?.desktop ?? false,
              },
              pagination: {
                enabled: pagination?.desktop ?? false,
              },
            },
          },
        });
      } catch (error) {
        console.error('Error initializing Swiper:', error);
      }
    }

    /**
     * Handle slide change event by pausing all videos
     * @returns {void}
     */
    _handleSlideChange() {
      const videos = this.querySelectorAll(`.${this.classes.video}`);
      videos.forEach((videoElement) => {
        const video = videoElement.querySelector('video');
        if (video && !video.hasAttribute('autoplay') && !video.paused) {
          videoElement.classList.remove(this.classes.playing);
          video.pause();
        }
      });
    }

    /**
     * Handle resize event with debouncing and apply fallback layout when needed
     * @returns {void}
     */
    _handleResize() {
      // Clear previous timeout
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      // Debounce resize handler
      this.resizeTimeout = setTimeout(() => {
        const totalSlides = this.elements.slides?.length || 0;
        const { slidesPerView, spaceBetween } = this._getSliderSettingsFromLiquid();

        const enoughSlides = !this._shouldUseFallbackLayout(slidesPerView, totalSlides);
        const isSwiperActive = !!this.swiperInstance && !this.swiperInstance.destroyed;

        if (enoughSlides && !isSwiperActive) {
          this.classList.remove(this.classes.fallback);
          this.style.removeProperty('--fallback-slides-per-view');
          this.style.removeProperty('--fallback-gap');
          this._init();
          return;
        }

        if (!enoughSlides && isSwiperActive) {
          this._destroy();
          this._applyFallbackLayout(slidesPerView, spaceBetween);
          return;
        }

        if (!enoughSlides) {
          this._applyFallbackLayout(slidesPerView, spaceBetween);
        }
      }, 150); // 150ms debounce delay
    }

    /**
     * Apply fallback layout when there are not enough slides
     * @param {object} slidesPerView - Slides per view configuration
     * @param {object} spaceBetween - Space between slides configuration
     * @returns {void}
     */
    _applyFallbackLayout(slidesPerView, spaceBetween) {
      const currentBreakpoint = window.innerWidth;

      let activeSlidesPerView = slidesPerView.base ?? 1.3;
      let gap = spaceBetween.base ?? 16;

      if (currentBreakpoint >= 1024) {
        activeSlidesPerView = slidesPerView.desktop ?? activeSlidesPerView;
        gap = spaceBetween.desktop ?? gap;
      } else if (currentBreakpoint >= 768) {
        activeSlidesPerView = slidesPerView.tablet ?? activeSlidesPerView;
        gap = spaceBetween.tablet ?? gap;
      }

      this.classList.add(this.classes.fallback);
      this.style.setProperty('--fallback-slides-per-view', activeSlidesPerView);
      this.style.setProperty('--fallback-gap', `${gap}px`);

      this.elements.navigations.forEach((nav) => {
        nav.classList.add(this.classes.hidden);
      });
    }

    /**
     * Check if fallback layout should be used based on total slides and slidesPerView
     * @param {object} slidesPerView - Slides per view configuration
     * @param {number} totalSlides - Total number of slides
     * @returns {boolean} - Whether fallback layout should be used
     */
    _shouldUseFallbackLayout(slidesPerView, totalSlides) {
      const currentWidth = window.innerWidth;

      let spv = slidesPerView.base ?? 1.3;
      if (currentWidth >= 1024) spv = slidesPerView.desktop ?? spv;
      else if (currentWidth >= 768) spv = slidesPerView.tablet ?? spv;

      return spv === 'auto' ? totalSlides <= 1 : totalSlides <= parseFloat(spv);
    }

    /**
     * Destroy the slider instance properly
     * @returns {void}
     */
    _destroy() {
      if (this.swiperInstance && !this.swiperInstance.destroyed) {
        try {
          this.swiperInstance.destroy(true, true); // deleteInstance, cleanStyles
          this.swiperInstance = null;
        } catch (error) {
          console.error('Error destroying Swiper instance:', error);
          this.swiperInstance = null;
        }
      }
    }

    /**
     * Get default slider settings
     * @returns {object} - Default settings object
     */
    _getDefaultSettings() {
      return {
        slidesPerView: { base: 1.3, tablet: 2, desktop: 4 },
        spaceBetween: { base: 16, tablet: 16, desktop: 24 },
        loop: { base: false, tablet: false, desktop: false },
        centeredSlides: { base: false, tablet: false, desktop: false },
        navigation: { base: false, tablet: false, desktop: false },
        pagination: { base: false, tablet: false, desktop: false },
        autoplay: { base: false, time: 3000 },
        insufficientSlidesPosition: { base: 'left', tablet: 'left', desktop: 'left' },
        customSettings: {},
      };
    }

    /**
     * Get slider settings from liquid with error handling
     * @returns {object} - Settings object from Liquid or defaults
     */
    _getSliderSettingsFromLiquid() {
      const settingsElement = document.getElementById(`${this.classes.sliderSettings}`);
      if (!settingsElement) {
        console.error(`Settings element not found: ${this.classes.sliderSettings}. Using default settings.`);
        return this._getDefaultSettings();
      }

      try {
        const settingsText = settingsElement.textContent.trim();

        if (!settingsText) {
          console.warn('Settings element is empty. Using default settings.');
          return this._getDefaultSettings();
        }

        const settings = JSON.parse(settingsText);

        // Merge with defaults to ensure all required properties exist
        return {
          ...this._getDefaultSettings(),
          ...settings,
          slidesPerView: { ...this._getDefaultSettings().slidesPerView, ...settings.slidesPerView },
          spaceBetween: { ...this._getDefaultSettings().spaceBetween, ...settings.spaceBetween },
          loop: { ...this._getDefaultSettings().loop, ...settings.loop },
          centeredSlides: { ...this._getDefaultSettings().centeredSlides, ...settings.centeredSlides },
          navigation: { ...this._getDefaultSettings().navigation, ...settings.navigation },
          pagination: { ...this._getDefaultSettings().pagination, ...settings.pagination },
          autoplay: { ...this._getDefaultSettings().autoplay, ...settings.autoplay },
          insufficientSlidesPosition: {
            ...this._getDefaultSettings().insufficientSlidesPosition,
            ...settings.insufficientSlidesPosition,
          },
        };
      } catch (error) {
        console.error('Failed to parse slider settings:', error);
        return this._getDefaultSettings();
      }
    }
  }

  customElements.define('wls-slider', WLSlider);
}
